import { ArrowRight, Building2, ContactRound, Mail, Phone, Plus, Search, UserRound, UsersRound } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { PageHeader, StateView, StatusBadge } from "../components";
import { useApiData } from "../hooks";
import { formatBrazilianPhone } from "../input-masks";
import type { CustomerPage } from "../types";

type CustomerStatus = "active" | "inactive" | "merged";

const statusLabels: Record<CustomerStatus, string> = { active: "Ativos", inactive: "Inativos", merged: "Mesclados" };

function contactValue(value: string | null | undefined, fallback = "Não informado") {
  return value?.trim() || fallback;
}

export function CustomersPage() {
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [status, setStatus] = useState<CustomerStatus>("active");
  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedSearch(search.trim()), 250);
    return () => window.clearTimeout(timer);
  }, [search]);
  const path = useMemo(() => `/customers?pageSize=100&status=${status}${debouncedSearch ? `&q=${encodeURIComponent(debouncedSearch)}` : ""}`, [debouncedSearch, status]);
  const { data, error, loading } = useApiData<CustomerPage>(path);
  const summary = data?.summary ?? { active: 0, companies: 0, individuals: 0, withoutContact: 0 };

  return <>
    <section className="page-intro"><div className="app-container"><PageHeader title="Clientes" action={<Link className="site-button site-button-navy" to="/clientes/novo"><Plus size={17} /> Novo cliente</Link>} /></div></section>
    <section className="app-section compact"><div className="app-container customer-directory-page">
      <section className="customer-summary-grid" aria-label="Resumo de clientes">
        <article><span className="customer-summary-icon is-blue"><ContactRound size={19} /></span><span>Ativos<strong>{summary.active}</strong></span></article>
        <article><span className="customer-summary-icon is-purple"><Building2 size={19} /></span><span>Empresas<strong>{summary.companies}</strong></span></article>
        <article><span className="customer-summary-icon is-green"><UsersRound size={19} /></span><span>Pessoas<strong>{summary.individuals}</strong></span></article>
        <article><span className="customer-summary-icon is-amber"><Phone size={19} /></span><span>Sem contato<strong>{summary.withoutContact}</strong></span></article>
      </section>
      <div className="customer-directory-controls">
        <div className="filter-tabs" aria-label="Status dos clientes">{(Object.keys(statusLabels) as CustomerStatus[]).map((value) => <button key={value} type="button" className={status === value ? "active" : ""} onClick={() => setStatus(value)}>{statusLabels[value]}</button>)}</div>
        <label className="site-search"><Search size={18} /><input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Buscar cliente" aria-label="Buscar por cliente, telefone ou e-mail" /></label>
      </div>
      <StateView loading={loading} error={error} empty={data?.data.length === 0}>
        <div className="customer-directory-grid">{data?.data.map((customer) => {
          const Icon = customer.kind === "company" ? Building2 : UserRound;
          return <article className="customer-directory-card" key={customer.id}>
            <header className="customer-directory-heading"><span className={`customer-directory-icon${customer.kind === "company" ? " is-company" : ""}`}><Icon size={20} /></span><div><span className="site-eyebrow">{customer.customerType}</span><h2>{customer.legalName}</h2></div><StatusBadge value={customer.status} /></header>
            <div className="customer-directory-contact">
              <span><Phone size={15} /> {customer.phone ? formatBrazilianPhone(customer.phone) : "Telefone não informado"}</span>
              <span><Mail size={15} /> {contactValue(customer.email, "E-mail não informado")}</span>
            </div>
            <div className="customer-directory-facts"><span>Cadastro<strong>{customer.kind === "company" ? "Pessoa jurídica" : "Pessoa física"}</strong></span><span>WhatsApp<strong>{customer.whatsapp ? formatBrazilianPhone(customer.whatsapp) : "Não informado"}</strong></span></div>
            <Link className="customer-directory-open" to={`/clientes/${customer.id}`}>Abrir cadastro <ArrowRight size={16} /></Link>
          </article>;
        })}</div>
      </StateView>
    </div></section>
  </>;
}
